import React from 'react';
import { bool } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { propTypes } from '../../util/types';
import { useHistory } from 'react-router-dom';
import { Page, LayoutSingleColumn } from '../../components';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../util/routes';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './CategoriesPage.module.css';

// Smart home categories data
const getCategoriesData = () => [
  {
    category: "Éclairage",
    description: "Solutions d'éclairage intelligent",
    icon: "💡",
    value: "lights"
  },
  {
    category: "Caméras de sécurité",
    description: "Sécurité et surveillance domestique",
    icon: "📹",
    value: "security-cameras"
  },
  {
    category: "Sonnettes connectées",
    description: "Systèmes de sonnette intelligente",
    icon: "🚪",
    value: "doorbells"
  },
  {
    category: "Capteurs IoT",
    description: "Surveillance environnementale",
    icon: "📡",
    value: "sensors"
  },
  {
    category: "Serrures Connectées",
    description: "Systèmes d'entrée sans clé",
    icon: "🔒",
    value: "smart-locks"
  },
  {
    category: "Stores & Rideaux",
    description: "Revêtements de fenêtres automatisés",
    icon: "🪟",
    value: "blind-and-curtains"
  },
  {
    category: "Systèmes d'alarme",
    description: "Protection du domicile et détection d'intrusion",
    icon: "🚨",
    value: "alarm-systems"
  },
  {
    category: "Têtes thermostatiques connectées",
    description: "Régulation pièce par pièce du chauffage",
    icon: "🎛️",
    value: "radiator-thermostats"
  },
  {
    category: "Thermostats connectés",
    description: "Pilotage intelligent de la température",
    icon: "🌡️",
    value: "smart-thermostats"
  },
  {
    category: "Prises connectées",
    description: "Contrôle à distance des appareils électriques",
    icon: "🔌",
    value: "smart-plugs"
  },
  {
    category: "Babyphones",
    description: "Surveillance bébé audio et vidéo",
    icon: "👶",
    value: "baby-monitor"
  },
  {
    category: "Robots tondeuses",
    description: "Tonte automatisée du jardin",
    icon: "🌿",
    value: "lawn-mowers"
  },
  {
    category: "Robots de piscine",
    description: "Nettoyage automatique du bassin",
    icon: "🏊",
    value: "robots-de-piscine",
    path: "/products/robots-de-piscine"
  },
  {
    category: "Enceintes et écrans",
    description: "Assistants vocaux et écrans intelligents",
    icon: "🔊",
    value: "smart-speakers-and-screens"
  },
  {
    category: "Enregistreurs vidéo réseau (NVR)",
    description: "Enregistrement centralisé pour caméras IP",
    icon: "💽",
    value: "network-video-recorders"
  }
];

export const CategoriesPageComponent = props => {
  const { inProgress, error } = props;
  const [searchTerm, setSearchTerm] = React.useState('');
  const intl = useIntl();
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const categoriesData = getCategoriesData();

  if (inProgress) {
    return (
      <Page
        title={intl.formatMessage({ id: "CategoriesPage.pageTitle" })}
        description={intl.formatMessage({ id: "CategoriesPage.pageDescription" })}
        scrollingDisabled={false}
      >
        <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
          <div className={css.root}>
            <div className={css.loading}>
              <FormattedMessage id="CategoriesPage.loadingCategories" />
            </div>
          </div>
        </LayoutSingleColumn>
      </Page>
    );
  }

  if (error) {
    return (
      <Page
        title={intl.formatMessage({ id: "CategoriesPage.pageTitle" })}
        description={intl.formatMessage({ id: "CategoriesPage.pageDescription" })}
        scrollingDisabled={false}
      >
        <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
          <div className={css.root}>
            <div className={css.error}>
              <h2>
                <FormattedMessage id="CategoriesPage.errorTitle" />
              </h2>
              <p>{error.message}</p>
            </div>
          </div>
        </LayoutSingleColumn>
      </Page>
    );
  }

  const handleCategoryClick = (category) => {
    if (category.path) {
      history.push(category.path);
      return;
    }
    const searchParams = { category: category.value };
    const to = createResourceLocatorString('SearchPage', routeConfiguration, {}, searchParams);
    history.push(to);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredCategories = categoriesData.filter(category =>
    category.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Page
      title={intl.formatMessage({ id: "CategoriesPage.pageTitle" })}
      description={intl.formatMessage({ id: "CategoriesPage.pageDescription" })}
      scrollingDisabled={false}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          <div className={css.container}>
            <div className={css.headerSection}>
              <h1 className={css.title}>
                <FormattedMessage id="CategoriesPage.title" />
              </h1>
              <p className={css.subtitle}>
                <FormattedMessage id="CategoriesPage.subtitle" />
              </p>
            </div>

            {/* Search Bar */}
            <div className={css.searchSection}>
              <div className={css.searchContainer}>
                <input
                  type="text"
                  placeholder={intl.formatMessage({ id: "CategoriesPage.searchPlaceholder" })}
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className={css.searchInput}
                />
                <div className={css.searchIcon}>🔍</div>
              </div>
              {searchTerm && (
                <div className={css.searchResults}>
                  <FormattedMessage 
                    id="CategoriesPage.searchResults" 
                    values={{ count: filteredCategories.length }}
                  />
                </div>
              )}
            </div>

            <div className={css.categoriesGrid}>
              {filteredCategories.map((category, index) => (
                <div
                  key={category.value}
                  className={css.categoryCard}
                  onClick={() => handleCategoryClick(category)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleCategoryClick(category);
                    }
                  }}
                >
                  <div className={css.categoryIconContainer}>
                    <span className={css.categoryIcon}>{category.icon}</span>
                  </div>
                  <h2 className={css.categoryName}>{category.category}</h2>
                  <p className={css.categoryDescription}>{category.description}</p>
                </div>
              ))}

              {searchTerm && filteredCategories.length === 0 && (
                <div className={css.noResults}>
                  <p>
                    <FormattedMessage 
                      id="CategoriesPage.noResults" 
                      values={{ searchTerm }}
                    />
                  </p>
                  <button
                    onClick={() => setSearchTerm('')}
                    className={css.clearSearchButton}
                  >
                    <FormattedMessage id="CategoriesPage.clearSearch" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

CategoriesPageComponent.propTypes = {
  inProgress: bool,
  error: propTypes.error,
};

const mapStateToProps = state => {
  // In a real app, you would fetch categories data from Redux state
  return {
    inProgress: false,
    error: null,
  };
};

const CategoriesPage = compose(connect(mapStateToProps))(CategoriesPageComponent);

export default CategoriesPage;
